import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  DecryptObjectFunction,
  EncryptObjectFunction,
} from "../../utils/cryptoFunction";
import { handleEncodeJson } from "../../utils/encodeDecodeJson";
import examActions from "../../Redux/exam/actions";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import duration from "dayjs/plugin/duration";
import RemainingTimeModal from "./remainingTimeModal";
import Cookies from "js-cookie";
import tickImage from "../../assets/images/tick.png";
import { CiLock } from "react-icons/ci";
import { PiClockCountdownFill } from "react-icons/pi";
import useFetchCandidateTokenDetails from "../../hooks/useFetchCandidateTokenDetails";
import commonActions from "../../Redux/common/actions";
import { auditLog } from "../../utils/auditLog";
import { ensureFullscreen } from "../../utils/fullScreenHelper";
dayjs.extend(utc);
dayjs.extend(duration);

export default function QuestionNumberComponent({
  questionsAndOptions,
  handleAuditLog,
  setNextQuestion,
  currentSectionTitle,
  remainingTimeOfIncrementalTime,
  formatTimeTimeOfIncrementalTime,
  onQuestionSelect,
  lastAnswerRef,
  editorInstanceRef,
  flushDebouncedSave,
  currentQuestionType,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState({});
  const [candidateTokenDetails, setCandidateTokenDetails] = useState(null);
  const [currentQuestionStatus, setCurrentQuestionStatus] = useState(null);
  const [currentFilter, setCurrentFilter] = useState("all");
  const [previousSectionIndex, setPreviousSectionIndex] = useState(-1);
  const [nextSection, setNextSection] = useState(null);
  const [initialRender, setInitialRender] = useState(true);
  const [visitedQuestions, setVisitedQuestions] = useState(new Set());
  const [currentSectionIndex, setCurrentSectionIndex] = useState(-1);
  const [remainingTime, setRemainingTime] = useState(0);
  const previousIndexRef = useRef(null);

  const lastUpdatedIndex = sessionStorage.getItem("lastUpdatedIndex");
  const isEarlyMovedSection = sessionStorage.getItem("isEarlyMovedSection");

  const isNoMovementTimeRestriction =
    candidateTokenDetails?.configuration?.sectionNavigation ==
    "NO_MOVEMENT_OR_TIME_RESTRICTION";
  const isQuestionNavigationForwardOnly =
    candidateTokenDetails?.configuration?.questionNavigationForwardOnly;
  const isFixedTimeMovementRestriction =
    candidateTokenDetails?.configuration?.sectionNavigation ==
    "FIXED_TIME_AND_MOVEMENT_RESTRICTION";
  const isMovementRestrictionIncrementalTime =
    candidateTokenDetails?.configuration?.sectionNavigation ==
    "MOVEMENT_RESTRICTION_WITH_INCREMENTAL_TIME";
  const isMovementRestrictionWithNoTimeRestriction =
    candidateTokenDetails?.configuration?.sectionNavigation ==
    "MOVEMENT_RESTRICTION_WITH_NO_TIME";

  let isRemainingTimeThere = localStorage.getItem("remainingTime");
  const examStartTime = localStorage.getItem("examStartTime");
  const isStartExamBetweenSpecifiedTime =
    candidateTokenDetails?.configuration?.startExamBetweenSpecifiedTime;
  const isForceLogout = candidateTokenDetails?.configuration?.forcedLogout;

  
  const { heartBeat, projectDetails } = useSelector(
    (state) => state.examReducer
  );

  const encryptedParams = searchParams.get("qNo");

  const decryptedParams = useMemo(() => {
    if (!encryptedParams) return {};
    try {
      return DecryptObjectFunction(encryptedParams);
    } catch (error) {
      console.error("Error decrypting parameters:", error);
      return {};
    }
  }, [encryptedParams]);

  const imageSize = decryptedParams?.img || "L";
  const language = decryptedParams?.lang || "english" || "hindi" || "marathi";
  const currentSrNo = decryptedParams?.SrNo || "defaultSrNo";
useEffect(() => {
    const storedVisited = sessionStorage.getItem("visitedQuestions");
    if (storedVisited) {
      setVisitedQuestions(new Set(JSON.parse(storedVisited)));
    }
    if (currentSrNo) {
      setVisitedQuestions((prev) => new Set([...prev, currentSrNo.toString()]));
    }
  }, [currentSrNo]);
  useFetchCandidateTokenDetails({ setCandidateTokenDetails });

  useEffect(() => {
    dispatch({
      type: examActions.GET_PROJECT_DETAILS,
      payload: {
        data: {
          project: `${Cookies.get("test_engine_project")}`,
        },
      },
    });
  }, []);

  const isSection = Object.values(
    questionsAndOptions?.questionsList || {}
  ).every((res) => "section" in res);

  const isSubSection = Object.values(
    questionsAndOptions?.questionsList || {}
  ).every((res) => "subSection" in res);

  useEffect(() => {
    if (questionsAndOptions?.questionsList) {
      const groupedData = Object.entries(
        questionsAndOptions.questionsList
      ).reduce((acc, [key, value]) => {
        if (!acc[value.section]) {
          acc[value.section] = [];
        }

        let status = value.status.toLowerCase();
        if (
          initialRender &&
          status === "unanswered" &&
          !visitedQuestions.has(key)
        ) {
          status = "not-viewed";
        }

        const questionData = {
          question_number: key,
          status: status,
        };

        if (isSection && isSubSection) {
          questionData.subSection = value.subSection;
        }

        acc[value.section].push(questionData);
        return acc;
      }, {});

      if (currentFilter !== "all") {
        Object.keys(groupedData).forEach((section) => {
          groupedData[section] = groupedData[section].filter(
            (q) => q.status === currentFilter
          );
        });
      }

      const filteredData = {
        [currentSectionTitle]: groupedData[currentSectionTitle] || [],
      };
      if (
        isNoMovementTimeRestriction ||
        isFixedTimeMovementRestriction ||
        isMovementRestrictionIncrementalTime ||
        isMovementRestrictionWithNoTimeRestriction
      ) {
        setData(filteredData);
      } else {
        setData(groupedData);
      }
      setNextQuestion(Object.values(groupedData).flat());

      if (initialRender) {
        setInitialRender(false);
      }
    }
  }, [
    questionsAndOptions?.questionsList,
    currentFilter,
    setNextQuestion,
    visitedQuestions,
    initialRender,
    isSection,
    isSubSection,
    currentSectionTitle,
  ]);

  const handleFilter = (key) => {
    setCurrentFilter((prevFilter) => {
      return prevFilter === key ? "all" : key;
    });
    const registrationNumber = candidateTokenDetails?.user?.id || "";
    const rollNumber = candidateTokenDetails?.user?.rollNo || "";
    const capitalizedFilter =
      key === "not_viewed"
        ? "Not Viewed"
        : key === "Answered_and_marked"
          ? "Answered and Marked"
          : key.charAt(0).toUpperCase() + key.slice(1);
    auditLog({
      data: {
        RegistrationNumber: registrationNumber,
        RollNumber: rollNumber,
        Activity: `User selected ${capitalizedFilter} Filter`,
        IPaddress: "",
      },
      dispatch,
      heartBeat,
    });
  };

  const getStatusCount = (status) => {
    const questionList = Object.entries(
      questionsAndOptions?.questionsList || {}
    ).map(([qNo, qData]) => ({
      qNo: parseInt(qNo),
      ...qData,
    }));

    const findCurrentSection = questionList.find(
      (arr) => arr.qNo === currentSrNo
    )?.section;

    const filteredQuestionList = questionList.filter(
      (arr) => arr.section === findCurrentSection
    );

    return (isSection ? filteredQuestionList : questionList).filter((q) => {
      // const questionNumber = Object.keys(
      //   questionsAndOptions.questionsList
      // ).find((key) => questionsAndOptions.questionsList[key] === q);

      // if (status === "not-viewed") {
      //   return (
      //     !visitedQuestions.has(questionNumber) &&
      //     (q.status.toLowerCase() === "unanswered" || !q.status)
      //   );
      // }

      // if (status === "unanswered") {
      //   return (
      //     visitedQuestions.has(questionNumber) &&
      //     q.status.toLowerCase() === "unanswered"
      //   );
      // }
      return q.status.toLowerCase() === status;
    }).length;
  };

  const handleQuestionNavigation = async (data) => {
    ensureFullscreen();
    flushDebouncedSave?.();
    const registrationNumber = candidateTokenDetails?.user?.id || "";
    const rollNumber = candidateTokenDetails?.user?.rollNo || "";
    auditLog({
      data: {
        RegistrationNumber: registrationNumber,
        RollNumber: rollNumber,
        Activity: `User selected Question No ${data.question_number}`,
        IPaddress: "",
      },
      dispatch,
      heartBeat,
    });
    const newVisited = new Set([...visitedQuestions, data.question_number]);
    setVisitedQuestions(newVisited);
    sessionStorage.setItem("visitedQuestions", JSON.stringify([...newVisited]));
    const formData = {
      imageSize: `${imageSize}`,
      action:
        currentQuestionStatus === "ANSWERED"
          ? "next"
          : currentQuestionStatus === "SKIPPED"
            ? "skip"
            : currentQuestionStatus === "MARKED" ||
              currentQuestionStatus === "ANSWERED_AND_MARKED"
              ? "mark"
              : "next",
      currentSrNo: currentSrNo,
      nextSrNo: parseInt(data?.question_number),
      selectedAnswer: questionsAndOptions?.questionData?.selectedAnswer || 0,
      language: `${language}`,
      ...(currentQuestionType === "DESCRIPTIVE_TYPING" && {
        descriptiveTypingAnswer:
          editorInstanceRef?.current?.getData?.() || lastAnswerRef?.current || "",
      }),
    };

    const encodedData = await handleEncodeJson({ encodeJSON: formData });
    const queryParams = {
      SrNo: parseInt(data?.question_number),
      lang: `${language}`,
      img: `${imageSize}`,
    };
    const encryptedParams = EncryptObjectFunction(queryParams);
    dispatch({
      type: examActions.GET_SUBMIT_ANSWER,
      payload: {
        data: encodedData,
        navigate: (path) => navigate(`${path}?qNo=${encryptedParams}`),
        mockMode: projectDetails?.mockMode,
      },
    });
    onQuestionSelect?.();
  };

  useEffect(() => {
    if (questionsAndOptions?.questionsList && currentSrNo) {
      const questionData = questionsAndOptions.questionsList[currentSrNo];
      setCurrentQuestionStatus(questionData ? questionData.status : null);
    }
  }, [currentSrNo, questionsAndOptions]);

  useEffect(() => {
    const totalSectionDurations =
      candidateTokenDetails?.configuration?.sections?.map((section) =>
        dayjs
          .duration({
            hours: Number(section.sectionTime.split(":")[0]),
            minutes: Number(section.sectionTime.split(":")[1]),
          })
          .asMilliseconds()
      ) || [];

    const calculateTime = () => {
      const now = dayjs
        .utc(heartBeat?.data)
        .subtract(5, "hours")
        .subtract(30, "minutes");
      const examStart = dayjs
        .utc(
          isStartExamBetweenSpecifiedTime
            ? examStartTime
            : candidateTokenDetails?.configuration?.startTime
        )
        .subtract(5, "hours")
        .subtract(30, "minutes");

      if (now.isBefore(examStart)) {
        setCurrentSectionIndex(-1);
        setRemainingTime(examStart.diff(now));
        return;
      }

      const elapsed = now.diff(examStart);

      if (totalSectionDurations.length === 0) {
        setCurrentSectionIndex(-1);
        setRemainingTime(0);
        return;
      }

      const accumulatedDurations = totalSectionDurations.reduce(
        (acc, duration, index) => {
          const total = acc.total + duration;
          if (!acc.found && elapsed < total) {
            acc.found = true;
            if (currentSectionIndex !== index) {
              setPreviousSectionIndex(currentSectionIndex);
              setCurrentSectionIndex(index);
              sessionStorage.setItem("lastUpdatedIndex", index);
              setNextSection(
                candidateTokenDetails?.configuration?.sections[index + 1]
                  ?.sectionTitle || null
              );
              handleSectionChange(index);
              if (
                previousIndexRef.current !== index &&
                nextSection &&
                !isMovementRestrictionIncrementalTime &&
                !isMovementRestrictionWithNoTimeRestriction
              ) {
                dispatch({
                  type: commonActions.SET_SHOW_MODAL,
                  payload: { show: true, type: null, method: "A", data: null },
                });
                auditLog({
                  data: {
                    RegistrationNumber: `${candidateTokenDetails?.user?.id}`,
                    RollNumber: `${candidateTokenDetails?.user?.rollNo}`,
                    Activity: `User Moved to ${nextSection} Section in Live Test`,
                    Timestamp: `${heartBeat?.date}`,
                  },
                  dispatch,
                  heartBeat,
                });
                previousIndexRef.current = index;
              }
            }
            // const timeDuration =
            //   candidateTokenDetails?.configuration?.timeDuration;
            // const [hours, minutes] = timeDuration.split(":").map(Number);
            // const totalMinutes = hours * 60 + minutes;
            // isMovementRestrictionIncrementalTime &&
            //   index > 0 &&
            //   isEarlyMovedSection &&
            //   Number(totalMinutes) - 2 >= Number(timeLeft?.totalMinutes)
            //   ? setRemainingTime(total - elapsed) : 
            setRemainingTime(total - elapsed);
            sessionStorage.setItem("remainingTime", total - elapsed);
          }
          return { total, found: acc.found };
        },
        { total: 0, found: false }
      );

      if (!accumulatedDurations.found) {
        setCurrentSectionIndex(
          candidateTokenDetails?.configuration?.sections?.length
        );
        setRemainingTime(0);
      }
    };

    const handleSectionChange = async (index) => {
      const currentSection =
        candidateTokenDetails?.configuration?.sections[index]?.sectionTitle;

      if (currentSection && questionsAndOptions?.questionsList) {
        const arr = Object.entries(questionsAndOptions.questionsList).map(
          ([key, val]) => ({
            qNo: parseInt(key),
            ...val,
          })
        );
        const qNo = arr.find((item) => item?.section === currentSection)?.qNo;

        const formData = {
          imageSize: `${imageSize}`,
          action: "next",
          currentSrNo: currentSrNo,
          nextSrNo: qNo,
          selectedAnswer:
            questionsAndOptions?.questionData?.selectedAnswer || 0,
          language: `${language}`,
        };

        try {
          const encodedData = await handleEncodeJson({ encodeJSON: formData });
          const queryParams = {
            SrNo: parseInt(qNo),
            lang: `${language}`,
            img: `${imageSize}`,
          };
          const encryptedParams = EncryptObjectFunction(queryParams);

          dispatch({
            type: examActions.GET_SUBMIT_ANSWER,
            payload: {
              data: encodedData,
              navigate: (path) => navigate(`${path}?qNo=${encryptedParams}`),
            },
          });
        } catch (error) {
          console.error("Error handling section change:", error);
        }
      }
    };

    const timer = setInterval(calculateTime, 1000);
    calculateTime();

    return () => clearInterval(timer);
  }, [
    candidateTokenDetails?.configuration?.sections,
    candidateTokenDetails?.configuration?.startTime,
    heartBeat,
    currentSectionIndex,
    currentSrNo,
    imageSize,
    language,
    isStartExamBetweenSpecifiedTime,
  ]);

  // useEffect(() => {
  //   const totalSectionDurations =
  //     candidateTokenDetails?.configuration?.sections.map((section) =>
  //       dayjs
  //         .duration({
  //           hours: Number(section.sectionTime.split(":")[0]),
  //           minutes: Number(section.sectionTime.split(":")[1]),
  //         })
  //         .asMilliseconds()
  //     );

  //   const calculateTime = () => {
  //     const now = dayjs();
  //     const examStart = dayjs
  //       .utc(candidateTokenDetails?.configuration?.startTime)
  //       .subtract(5, "hours")
  //       .subtract(30, "minutes");

  //     if (now.isBefore(examStart)) {
  //       setCurrentSectionIndex(-1);
  //       setRemainingTime(examStart.diff(now));
  //       return;
  //     }

  //     const elapsed = now.diff(examStart);
  //     let accumulatedTime = 0;

  //     for (
  //       let i = 0;
  //       i < candidateTokenDetails?.configuration?.sections?.length;
  //       i++
  //     ) {
  //       const sectionDuration = totalSectionDurations[i];

  //       if (elapsed < accumulatedTime + sectionDuration) {
  //         if (currentSectionIndex !== i) {
  //           setPreviousSectionIndex(currentSectionIndex);
  //           setCurrentSectionIndex(i);

  //           if (currentSectionIndex >= 0) {
  //             setCurrentSection(
  //               candidateTokenDetails?.configuration?.sections[
  //                 currentSectionIndex
  //               ].sectionTitle
  //             );
  //             setNextSection(
  //               candidateTokenDetails?.configuration?.sections[i].sectionTitle
  //             );
  //             const handleSectionChange = async (currentSectionIndex, i) => {
  //               const currentSection =
  //                 candidateTokenDetails?.configuration?.sections[i]
  //                   .sectionTitle;

  //               if (currentSection && questionsAndOptions?.questionsList) {
  //                 const obj = questionsAndOptions.questionsList;
  //                 const arr = Object.entries(obj).map(([key, val]) => ({
  //                   qNo: parseInt(key),
  //                   ...val,
  //                 }));
  //                 const qNo = arr.filter(
  //                   (item) => item?.section === currentSection
  //                 )[0]?.qNo;

  //                 const formData = {
  //                   imageSize: `${imageSize}`,
  //                   action: "next",
  //                   currentSrNo: currentSrNo,
  //                   nextSrNo: qNo,
  //                   selectedAnswer:
  //                     questionsAndOptions?.questionData?.selectedAnswer || 0,
  //                   language: `${language}`,
  //                 };
  //                 try {
  //                   const encodedData = await handleEncodeJson({
  //                     encodeJSON: formData,
  //                   });
  //                   const queryParams = {
  //                     SrNo: parseInt(qNo),
  //                     lang: `${language}`,
  //                     img: `${imageSize}`,
  //                   };
  //                   const encryptedParams = EncryptObjectFunction(queryParams);

  //                   dispatch({
  //                     type: examActions.GET_SUBMIT_ANSWER,
  //                     payload: {
  //                       data: encodedData,
  //                       navigate: (path) =>
  //                         navigate(`${path}?qNo=${encryptedParams}`),
  //                     },
  //                   });
  //                 } catch (error) {
  //                   console.error("Error handling section change:", error);
  //                 }
  //               }
  //             };
  //             handleSectionChange(currentSectionIndex, i);
  //             dispatch({
  //               type: commonActions.SET_SHOW_MODAL,
  //               payload: { show: true, type: null, method: "A", data: null },
  //             });
  //           }
  //         }
  //         setRemainingTime(accumulatedTime + sectionDuration - elapsed);
  //         return;
  //       }
  //       accumulatedTime += sectionDuration;
  //     }
  //     setCurrentSectionIndex(
  //       candidateTokenDetails?.configuration?.sections?.length
  //     );
  //     setRemainingTime(0);
  //   };

  //   const timer = setInterval(calculateTime, 1000);
  //   calculateTime();
  //   return () => clearInterval(timer);
  // }, [
  //   candidateTokenDetails?.configuration?.sections,
  //   candidateTokenDetails?.configuration?.startTime,
  //   heartBeat,
  //   currentSectionIndex,
  //   currentSrNo,
  //   imageSize,
  //   language,
  //   questionsAndOptions,
  // ]);

  const formatTime = (ms) => {
    const duration = dayjs.duration(ms);
    const hours = duration.hours().toString().padStart(2, "0");
    const minutes = duration.minutes().toString().padStart(2, "0");
    const seconds = duration.seconds().toString().padStart(2, "0");
    return `${hours}:${minutes}:${String(timeLeft?.seconds).padStart(2, "0") || seconds
      }`;
  };

  const statusConfigs = [
    {
      key: "answered",
      label: "Answered",
      style: {
        width: "22.82px",
        height: "22.82px",
        background: "rgba(84, 179, 55, 1)",
        clipPath:
          "polygon(25% 0%, 75% 0%, 100% 50%, 100% 100%, 0 100%, 0% 50%)",
      },
    },
    {
      key: "unanswered",
      label: "Unanswered",
      style: {
        width: "22.82px",
        height: "22.82px",
        background: "rgba(234, 104, 58, 1)",
        clipPath: "polygon(0 0, 100% 0, 100% 50%, 75% 100%, 25% 100%, 0% 50%)",
      },
    },
    {
      key: "not_viewed",
      label: "Not Viewed",
      style: {
        width: "22.82px",
        height: "22.82px",
        background:
          "linear-gradient(45deg, rgba(240, 239, 240, 1), rgba(201, 200, 201, 1))",
      },
    },
    {
      key: "marked",
      label: "Marked for Review",
      style: {
        width: "22.82px",
        height: "22.82px",
        background: "rgba(130, 91, 172, 1)",
        borderRadius: "50%",
      },
    },
    {
      key: "answered_and_marked",
      label: "Answered & Marked for Review",
      style: {
        width: "22.82px",
        height: "22.82px",
        background: "rgba(130, 91, 172, 1)",
        borderRadius: "50%",
        position: "relative",
      },
    },
  ];

  const getQuestionStyle = (
    questionStatus,
    isCurrentQuestion,
    questionNumber,
    visitedQuestions
  ) => {
    if (isCurrentQuestion) {
      return {
        background: "#808080",
      };
    }

    if (
      !visitedQuestions.has(questionNumber.toString()) &&
      (questionStatus?.toLowerCase() === "unanswered" || !questionStatus)
    ) {
      return {
        // background:
        //   "linear-gradient(45deg, rgba(240, 239, 240, 1), rgba(201, 200, 201, 1))",
        background: "rgba(234, 104, 58, 1)",
          clipPath:
            "polygon(0 0, 100% 0, 100% 50%, 75% 100%, 25% 100%, 0% 50%)",
      };
    }

    switch (questionStatus?.toLowerCase()) {
      case "answered":
        return {
          background: "rgba(84, 179, 55, 1)",
          clipPath:
            "polygon(25% 0%, 75% 0%, 100% 50%, 100% 100%, 0 100%, 0% 50%)",
        };
      case "unanswered":
        return {
          background: "rgba(234, 104, 58, 1)",
          clipPath:
            "polygon(0 0, 100% 0, 100% 50%, 75% 100%, 25% 100%, 0% 50%)",
        };
      case "marked":
        return {
          background: "rgba(130, 91, 172, 1)",
          borderRadius: "50%",
        };
      case "answered_and_marked":
        return {
          background: "rgba(130, 91, 172, 1)",
          borderRadius: "50%",
        };
      default:
        return {
          background:
            "linear-gradient(45deg, rgba(240, 239, 240, 1), rgba(201, 200, 201, 1))",
        };
    }
  };

  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const checkFullscreen = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    checkFullscreen();

    document.addEventListener("fullscreenchange", checkFullscreen);

    return () => {
      document.removeEventListener("fullscreenchange", checkFullscreen);
    };
  }, []);

  const currentQuestionRef = useRef(null);
  useEffect(() => {
    if (currentQuestionRef.current) {
      currentQuestionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentSrNo]);

  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
    totalMinutes: 0,
  });

  const examEndTime = localStorage.getItem("examEndTime");
  const examEndDate = new Date(examEndTime);
  examEndDate.setTime(examEndDate.getTime() + (5 * 60 + 30) * 60 * 1000);
  const updatedExamEndTime = examEndDate.toISOString();

  useEffect(() => {
    const startTime = Date.parse(
      isStartExamBetweenSpecifiedTime
        ? examStartTime
        : candidateTokenDetails?.configuration?.startTime
    );
    const endTime =
      // ((isLateLoginAllowed && isMaximumLoginAttempts ) || (isMultidayExamWithSection && isMaximumLoginAttempts))
      //   ? Date.parse(candidateTokenDetails?.configuration?.endTime)
      //   :
      isForceLogout
        ? Date.parse(candidateTokenDetails?.configuration?.endTime)
        : Date.parse(
          updatedExamEndTime ?? candidateTokenDetails?.configuration?.endTime
        );

    const updateTimer = () => {
      const currentTime = new Date(heartBeat?.data);

      if (currentTime < startTime) {
        const totalDuration = endTime - startTime;
        const hours = Math.floor(totalDuration / (1000 * 60 * 60));
        const minutes = Math.floor(
          (totalDuration % (1000 * 60 * 60)) / (1000 * 60)
        );
        const totalSeconds = Math.floor(totalDuration / 1000);
        const seconds = totalSeconds % 60;
        setTimeLeft({ hours, minutes, seconds });
        return;
      }

      if (currentTime >= endTime) {
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      if (currentTime >= startTime) {
        const difference = endTime - currentTime;
        const hours = Math.floor(difference / (1000 * 60 * 60));
        const minutes = Math.floor(
          (difference % (1000 * 60 * 60)) / (1000 * 60)
        );
        const totalSeconds = Math.floor(difference / 1000);
        const seconds = totalSeconds % 60;
        const totalMinutes = Math.floor(difference / (1000 * 60));
        setTimeLeft({ hours, minutes, seconds, totalMinutes });
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [candidateTokenDetails, heartBeat]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Grid
          sx={{
            backgroundColor: "rgba(240, 240, 251, 1)",
            height: "32px",
            borderRadius: "8px",
          }}
          p={1}
        >
          <Typography
            sx={{
              color: "rgba(63, 65, 209, 1)",
              fontSize: "14px",
              fontWeight: "bold",
              textAlign:"center"
            }}
          >
            Summary
          </Typography>
        </Grid>
        <Grid
          container
          sx={{ display: "flex", flexDirection: "column", rowGap: "12px" }}
          p={2}
        >
          {statusConfigs.map((config) => {
            const isSummaryFilterActive = currentFilter === config.key;
            return ( 
              <Box
                key={config.key}
                sx={{
                  display: "flex",
                  alignItems: 'center',
                  justifyContent: "space-between",
                  cursor: "pointer",
                  border: isSummaryFilterActive ? '1px solid #ccc' : '',
                  borderRadius:  isSummaryFilterActive ? '8px' : 0,
                  p: isSummaryFilterActive ? '6px' : ''
                }}
                onClick={() => {
                  handleFilter(config.key);
                }}
              >
                <Typography sx={{
                  fontSize: "14px",
                  color: isSummaryFilterActive ? 'rgba(63, 65, 209, 1)' : '',
                  fontWeight: isSummaryFilterActive ? '500' : '400'
                }}>
                  {config.label}
                </Typography>
                <Box
                  sx={{
                    ...config.style,
                    color: "#000",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    fontFamily: "monospace",
                    padding: '15px'
                  }}
                >
                  {getStatusCount(config.key)}
                  {config.key === "answered_and_marked" && (
                    <Box
                      component="img"
                      alt="check"
                      src={tickImage}
                      sx={{
                        position: "absolute",
                        bottom: -1,
                        right: -4,
                        height: "13px",
                        width: "auto",
                      }}
                    />
                  )}
                </Box>
              </Box>
            )
          }
        )}
        </Grid>
      </Grid>
      {!isSection && (
        <Grid item xs={12} pb={1}>
          <Divider />
        </Grid>
      )}
      <Grid
        item
        xs={12}
        sx={{
          height: isFullscreen ? "330px" : "305px",
          overflowY: "auto",
          paddingRight: "8px",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#e0e0e0",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "blue",
            borderRadius: "10px",
            border: "2px solid #e0e0e0",
            height: "10px",
            paddingBottom: "12px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "darkblue",
          },
        }}
      >
        {Object.entries(data).map(([section, questions], index) => {
          return (
            <Box key={section} sx={{ mb: 2 }}>
              {isSection && (
                <Box
                  p={1}
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                    backgroundColor: "rgba(240, 240, 251, 1)",
                    height: "32px",
                    borderRadius: "8px",
                  }}
                >
                  <Typography
                    sx={{
                      color: "rgba(63, 65, 209, 1)",
                      fontSize: "14px",
                      fontWeight: "bold",
                      width: '100px',
                       whiteSpace: "nowrap", 
                    }}
                  >
                    {section === "null" ? "Loading..." : section}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ width: "100%", textWrap: "nowrap" }}
                  >
                    {/* {isNoMovementTimeRestriction ||
                    isMovementRestrictionIncrementalTime
                      ? null
                      : isFixedTimeMovementRestriction
                      ? index < currentSectionIndex && (
                          <p>Time's up for this section!</p>
                        )
                      : null} */}
                    {isNoMovementTimeRestriction
                      ? null
                      : isFixedTimeMovementRestriction ||
                        isMovementRestrictionIncrementalTime
                        ? section === currentSectionTitle && (
                          // ? index === currentSectionIndex
                          <Box
                            style={{
                              color: "rgba(63, 65, 209, 0.7)",
                              display: "flex",
                              justifyContent: "flex-end",
                              alignItems: "center",
                              color: "red",
                              fontWeight: "bold",
                              gap: "4px",
                            }}
                          >
                            <PiClockCountdownFill
                              size={22}
                              style={{ margin: 0, padding: 0 }}
                              color="red"
                            />
                            {isMovementRestrictionIncrementalTime &&
                              parseInt(lastUpdatedIndex) > 1 ? (
                              <p>
                                {String(timeLeft?.hours).padStart(2, "0")}:
                                {String(timeLeft?.minutes).padStart(2, "0")}:
                                {String(timeLeft?.seconds).padStart(2, "0")}
                              </p>
                            ) : (
                              <p>{formatTime(remainingTime)}</p>
                            )}
                          </Box>
                        )
                        : null}
                    {isNoMovementTimeRestriction
                      ? null
                      : isFixedTimeMovementRestriction ||
                        isMovementRestrictionIncrementalTime
                        ? index > currentSectionIndex && (
                          <Box
                            sx={{
                              color: "rgba(63, 65, 209, 0.7)",
                              display: "flex",
                              gap: "1%",
                              justifyContent: "flex-end",
                              alignItems: "center",
                            }}
                          >
                            <CiLock
                              size={18}
                              style={{ margin: 0, padding: 0 }}
                            />
                            <p>Section is locked</p>
                          </Box>
                        )
                        : null}
                  </Typography>
                </Box>
              )}

              {!isSection || isSection
                ? Object.entries(
                  questions.reduce((acc, q) => {
                    if (!acc[q.subSection]) {
                      acc[q.subSection] = [];
                    }
                    acc[q.subSection].push(q);
                    return acc;
                  }, {})
                ).map(([subSection, subSectionQuestions]) => (
                  <Box key={subSection} sx={{ mb: 2 }}>
                    {/* <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                          backgroundColor: "#fff",
                          height: "32px",
                          borderRadius: "8px",
                          border: "1px solid rgba(63, 65, 209, 0.8)",
                        }}
                      > */}
                    {isSubSection && (
                      <>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: "bold",
                            color: "rgba(63, 65, 209, 0.8)",
                            mt: 1,
                          }}
                        >
                          {subSection}
                        </Typography>
                        <Divider
                          sx={{ mb: 1, color: "rgba(63, 65, 209, 0.8)" }}
                        />
                      </>
                    )}

                    {/* </Box> */}
                    <Grid
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(5, 1fr)",
                        gap: 2,
                      }}
                    >
                      {subSectionQuestions.map((obj) => {
                        const isCurrentQuestion =
                          currentSrNo === parseInt(obj.question_number);
                        let displayStatus = obj.status;
                        if (
                          !visitedQuestions.has(obj.question_number) &&
                          obj.status === "unanswered"
                        ) {
                          displayStatus = "not-viewed";
                        }

                        const baseStyles = getQuestionStyle(
                          obj.status,
                          isCurrentQuestion,
                          obj.question_number,
                          visitedQuestions
                        );

                        return (
                          <Box
                            ref={
                              isCurrentQuestion ? currentQuestionRef : null
                            }
                            key={obj.question_number}
                            onClick={() => {
                              if(!isQuestionNavigationForwardOnly){
                               handleQuestionNavigation(obj); 
                              }
                              if (
                                !isQuestionNavigationForwardOnly &&
                                isNoMovementTimeRestriction ||
                                isMovementRestrictionIncrementalTime ||
                                isMovementRestrictionWithNoTimeRestriction
                              ) {
                                if (
                                  !isSection ||
                                  (isNoMovementTimeRestriction &&
                                    !isFixedTimeMovementRestriction)
                                ) {
                                  handleQuestionNavigation(obj);
                                } else if (section === currentSectionTitle) {
                                  handleQuestionNavigation(obj);
                                }
                              }
                            }}
                            sx={{
                              ...baseStyles,
                              width: "25px",
                              height: "25px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "14px",
                              cursor: isQuestionNavigationForwardOnly ? 'not-allowed' :
                                (isSection &&
                                  !isNoMovementTimeRestriction &&
                                  isFixedTimeMovementRestriction) ||
                                  isMovementRestrictionIncrementalTime ||
                                  isMovementRestrictionWithNoTimeRestriction
                                  ? section === currentSectionTitle
                                    ? // ? index === currentSectionIndex
                                    "pointer"
                                    : "not-allowed"
                                  : "pointer",
                              opacity:
                                (isSection &&
                                  !isNoMovementTimeRestriction &&
                                  isFixedTimeMovementRestriction) ||
                                  isMovementRestrictionIncrementalTime ||
                                  isMovementRestrictionWithNoTimeRestriction
                                  ? section === currentSectionTitle
                                    ? // ? index === currentSectionIndex
                                    1
                                    : 0.5
                                  : 1,
                              position: "relative",
                              "&:hover": {
                                transform: isCurrentQuestion
                                  ? "none"
                                  : "scale(1.05)",
                                transition: "transform 0.2s ease-in-out",
                              },
                            }}
                          >
                            {obj.question_number}
                            {obj.status === "answered_and_marked" && (
                              <Box
                                component="img"
                                alt="check"
                                src={tickImage}
                                sx={{
                                  position: "absolute",
                                  bottom: -1,
                                  right: -4,
                                  height: "13px",
                                  width: "auto",
                                }}
                              />
                            )}
                          </Box>
                        );
                      })}
                    </Grid>
                  </Box>
                ))
                : null}
            </Box>
          );
        })}
      </Grid>
      {candidateTokenDetails?.configuration?.sections?.length > 0 && (
        <RemainingTimeModal
          currentSection={
            candidateTokenDetails?.configuration?.sections[
              currentSectionIndex - 1
            ]?.sectionTitle
          }
          nextSection={
            candidateTokenDetails?.configuration?.sections[currentSectionIndex]
              ?.sectionTitle
          }
        />
      )}
    </Grid>
  );
}
